import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import {
  Card,
  CardContent,
  Typography,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  Fade,
  Paper,
  Snackbar,
  styled,
  IconButton,
  Menu,
  Button,
  Tooltip,
  Collapse,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CellTowerIcon from '@mui/icons-material/CellTower';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CurrencySelector from './currencySelector';
import CopyButton from './CopyButton';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import FAQs from './FAQs';
import PaymentsIcon from '@mui/icons-material/Payments';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import ViewPayments from './ViewPayments';
import FormComponent from './FormComponent';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SolanaWalletButton from './SolanaWalletButton';
import BNBWalletButton from './BNBWalletButton';
import { NETWORK_TOKENS } from '../utils/networkTokens';
import debugLog from '../utils/debugLog';
import { formatDateTime, toEpochMs } from '../utils/time';
import WalletStatusChip from './WalletStatusChip';
import BaseWalletButton from './BASEWalletButton';
import NetworkTokenSelector from './NetworkTokenSelector';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PaymentInfo from './paymentInfo';
import TokenLogo from './TokenLogo';


const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 500,
  margin: '2rem auto',
  position: 'relative',
  minHeight: 400,
  transition: 'all 0.3s ease',
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  borderRadius: '16px',
  background: 'linear-gradient(to bottom, #ffffff, #fafafa)'
}));

const ContentWrapper = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(3),
}));

const LogoWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(2),
  width: 60,
  height: 60,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  }
}));

const CompanyHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3.5, 3, 2),
  borderBottom: '1px solid #eaeaea',
  marginBottom: theme.spacing(0),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  '& .company-name': {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  '& .transaction-id': {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(0.3),
      '& .label': {
        fontSize: '0.8rem',
        fontWeight: 500,
      },
      '& .id': {
        fontSize: '0.75rem',
        opacity: 0.8,
        letterSpacing: '0.02em',
      }
    }
  }
}));

const PaymentAmount = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  '& .amount': {
    fontSize: '2.5rem',
    fontWeight: 700,
    color: '#000000',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
  },
  '& .description': {
    color: theme.palette.text.secondary,
    fontSize: '1rem',
    maxWidth: '80%',
    margin: '0 auto',
    lineHeight: 1.5
  }
}));

const NetworkChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.8),
  padding: '5px 10px',
  borderRadius: '12px',
  backgroundColor: '#FEEFC3',
  fontSize: '0.8rem',
  fontWeight: 500,
  '& .network-icon': {
    color: '#000000',
    fontSize: '1rem'
  }
}));

const TokenChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.8),
  padding: '5px 10px',
  borderRadius: '12px',
  backgroundColor: '#E3F2FD',
  fontSize: '0.8rem',
  fontWeight: 500,
  '& .token-icon': {
    color: '#000000',
    fontSize: '1rem'
  }
}));

const ChangeNetworkButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: '5px 12px',
  backgroundColor: '#FFF3E0',
  color: '#E65100',
  textTransform: 'none',
  fontSize: '0.8rem',
  fontWeight: 500,
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: '#FFE0B2',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    padding: '4px 10px',
  }
}));

const TestnetChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: '4px 8px',
  borderRadius: '12px',
  backgroundColor: '#FFF8E1',
  color: '#F57C00',
  fontSize: '0.75rem',
  fontWeight: 500,
  cursor: 'help',
  '& .icon': {
    fontSize: '1rem',
    marginLeft: '2px'
  }
}));

const PaymentCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isPrimary'
})(({ theme, isPrimary }) => ({
  backgroundColor: '#fff',
  borderRadius: 20,
  overflow: 'hidden',
  boxShadow: isPrimary 
    ? '0 6px 24px rgba(103, 126, 234, 0.15)'
    : '0 2px 12px rgba(0, 0, 0, 0.06)',
  border: isPrimary 
    ? '2px solid rgba(103, 126, 234, 0.12)'
    : '1px solid rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  marginBottom: theme.spacing(1.5),
  transform: isPrimary ? 'scale(1.02)' : 'scale(1)',
  '&:hover': {
    transform: isPrimary ? 'scale(1.02) translateY(-2px)' : 'translateY(-1px)',
    boxShadow: isPrimary 
      ? '0 8px 30px rgba(103, 126, 234, 0.15)'
      : '0 4px 16px rgba(0, 0, 0, 0.08)',
  }
}));

const PaymentCardHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'expanded'
})(({ theme, expanded }) => ({
  padding: theme.spacing(2.5, 3),
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  userSelect: 'none',
  borderBottom: expanded ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  '& .header-content': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  '& .icon-wrapper': {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 24,
  },
  '& .text-content': {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  '& .title': {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    letterSpacing: '-0.02em',
  },
  '& .subtitle': {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    fontWeight: 400,
  }
}));

const PaymentCardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}));

const ExpandIcon = styled(ExpandMoreIcon, {
  shouldForwardProp: (prop) => prop !== 'expanded'
})(({ theme, expanded }) => ({
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  color: theme.palette.text.secondary,
  fontSize: 24,
}));

const PaymentMethodWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.2),
  marginTop: theme.spacing(0),
}));

const QRAddressWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: 'rgba(249, 250, 251, 0.5)',
  borderRadius: 12,
  border: '1px solid rgba(0, 0, 0, 0.06)',
}));

const PaymentFooter = styled(Box)(({ theme }) => ({
  display: 'flex', 
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: theme.spacing(2),
  paddingX: theme.spacing(2),
  paddingY: theme.spacing(1.5),
  backgroundColor: 'rgba(248, 250, 252, 0.4)',
  borderRadius: 16,
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.03)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(248, 250, 252, 0.5)',
    borderColor: 'rgba(0, 0, 0, 0.06)',
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingY: theme.spacing(1.5),
  }
}));

const CompactHelpButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  width: 32,
  height: 32,
  '&:hover': {
    backgroundColor: '#e9ecef',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.1rem',
  }
}));

const getNetworkDisplayName = (network) => {
  const networkDisplayNames = {
    'bsc': 'BNB',
    'base': 'BASE',
    'solana': 'SOLANA'
  };
  return networkDisplayNames[network] || network.toUpperCase();
};

const normalizeTokenId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const QUOTE_REFRESH_AFTER_EXPIRY_MS = 2000;
const WS_IDLE_TIMEOUT_MS = 3 * 60 * 1000;
const WS_MAX_LIFETIME_MS = 6 * 60 * 1000;
const WS_RECONNECT_BASE_DELAY_MS = 1000;
const WS_RECONNECT_MAX_DELAY_MS = 8000;
const WS_RECONNECT_MAX_ATTEMPTS = 3;
const CHECK_PAYMENT_COOLDOWN_MS = 15000;
const TOKENS_REFETCH_COOLDOWN_MS = 2000;
const AUTH_ERROR_STATES = new Set(['expired', 'session_expired', 'invalid_signature', 'unauthorized']);

const readSessionFromLocation = () => {
  if (typeof window === 'undefined') {
    return { token: null, signature: null };
  }

  const params = new URLSearchParams(window.location.search);
  let token = params.get('session');
  let signature = params.get('signature');

  if ((!token || !signature) && window.location.hash) {
    const hashQueryIndex = window.location.hash.indexOf('?');
    if (hashQueryIndex !== -1) {
      const hashParams = new URLSearchParams(window.location.hash.slice(hashQueryIndex + 1));
      token = token || hashParams.get('session');
      signature = signature || hashParams.get('signature');
    }
  }

  return {
    token: (token || '').trim() || null,
    signature: (signature || '').trim() || null
  };
};

const PaymentIframe = () => {
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [paymentAddress, setPaymentAddress] = useState(null);
  const [error, setError] = useState(null);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [selectedToken, setSelectedToken] = useState(null);
  const [availableTokens, setAvailableTokens] = useState([]);
  const [tokenDetails, setTokenDetails] = useState(null);
  const [priceValidUntil, setPriceValidUntil] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [isQuoteExpired, setIsQuoteExpired] = useState(false);
  const [isQuoteFrozen, setIsQuoteFrozen] = useState(false);
  const [isRefreshingQuote, setIsRefreshingQuote] = useState(false);
  const [pollingEnabled, setPollingEnabled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [contactAnchorEl, setContactAnchorEl] = useState(null);
  const contactOpen = Boolean(contactAnchorEl);
  const [helpAnchorEl, setHelpAnchorEl] = useState(null);
  const helpMenuOpen = Boolean(helpAnchorEl);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showFAQs, setShowFAQs] = useState(false);
  const [showPayments, setShowPayments] = useState(false);
  const [questions, setQuestions] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [txState, setTxState] = useState({
    status: 'idle',
    hash: null,
    explorerUrl: null,
    network: null,
    tokenSymbol: null,
    amount: null,
    error: null
  });
  const [manualVerifyGateOpen, setManualVerifyGateOpen] = useState(false);
  const [checkPaymentUi, setCheckPaymentUi] = useState({ status: 'idle' });
  const [walletInfo, setWalletInfo] = useState({
    isConnected: false,
    address: null,
    networkName: '',
    tokenSymbol: '',
    isTestnet: false,
    walletType: 'Wallet',
    balance: null
  });
  const [isQuickPayExpanded, setIsQuickPayExpanded] = useState(true);
  const [isManualPayExpanded, setIsManualPayExpanded] = useState(false);
  const wsRef = useRef(null);
  const closeWebSocketRef = useRef(null);
  const sessionInvalidRef = useRef(false);
  const lastSessionRef = useRef({ token: null, signature: null });
  const wsIdleTimeoutRef = useRef(null);
  const wsMaxLifetimeTimeoutRef = useRef(null);
  const wsReconnectTimeoutRef = useRef(null);
  const wsReconnectAttemptsRef = useRef(0);
  const wsLastEventIdRef = useRef(null);
  const wsConnectionDetailsRef = useRef(null);
  const wsManualCloseRef = useRef(false);
  const connectWebSocketRef = useRef(null);
  const quoteRefreshTimeoutRef = useRef(null);
  const expiredRefreshCooldownRef = useRef(0);
  const refreshInFlightRef = useRef(false);
  const pollingIntervalRef = useRef(null);
  const refreshQuoteRef = useRef(null);
  const isQuoteFrozenRef = useRef(false);
  const partialLockRef = useRef({ network: null, tokenId: null, remainingUsd: null });
  const lastVerifiedTxRef = useRef(null);
  const verifyInFlightRef = useRef(false);
  const manualVerifyRef = useRef(null);
  const txFallbackTimerRef = useRef(null);
  const txFallbackAttemptedRef = useRef(new Set());
  const checkPaymentCooldownRef = useRef(null);
  const returnRedirectedRef = useRef(false);
  const tokensFetchInFlightRef = useRef(false);
  const tokensLastFetchedRef = useRef(0);

  const invoiceStatus = String(paymentDetails?.invoice_status || '').toLowerCase();
  const isUnderpaid = invoiceStatus === 'underpaid' || invoiceStatus === 'partial';
  const lockedNetwork = paymentDetails?.locked_network || null;
  const lockedTokenId = paymentDetails?.locked_token_id ?? null;
  const { token: sessionToken, signature: sessionSignature } = readSessionFromLocation();
  const invoiceId = paymentDetails?.invoice_id || null;

  const markSessionInvalid = useCallback((code) => {
    if (sessionInvalidRef.current) {
      if (AUTH_ERROR_STATES.has(code)) {
        setError(code);
      }
      return;
    }
    sessionInvalidRef.current = true;
    setError(code);
    setPollingEnabled(false);
    setIsRefreshingQuote(false);
    setIsQuoteFrozen(true);
    setManualVerifyGateOpen(false);
    setCheckPaymentUi({ status: 'idle' });
    refreshInFlightRef.current = false;
    if (quoteRefreshTimeoutRef.current) {
      clearTimeout(quoteRefreshTimeoutRef.current);
      quoteRefreshTimeoutRef.current = null;
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (closeWebSocketRef.current) {
      closeWebSocketRef.current('session_invalid');
    }
  }, []);

  const resetSessionState = useCallback((nextSession = {}) => {
    lastSessionRef.current = {
      token: nextSession.token ?? null,
      signature: nextSession.signature ?? null
    };
    sessionInvalidRef.current = false;
    setError(null);
    setPaymentDetails(null);
    setPaymentAddress(null);
    setAvailableTokens([]);
    setSelectedNetwork(null);
    setSelectedToken(null);
    setTokenDetails(null);
    setPriceValidUntil(null);
    setCountdown(null);
    setIsQuoteExpired(false);
    setIsQuoteFrozen(false);
    setIsRefreshingQuote(false);
    setPollingEnabled(false);
    setQuestions(null);
    setShowForm(false);
    setFormSubmitted(false);
    setManualVerifyGateOpen(false);
    setCheckPaymentUi({ status: 'idle' });
    setTxState({
      status: 'idle',
      hash: null,
      explorerUrl: null,
      network: null,
      tokenSymbol: null,
      amount: null,
      error: null
    });
    refreshInFlightRef.current = false;
    verifyInFlightRef.current = false;
    lastVerifiedTxRef.current = null;
    partialLockRef.current = { network: null, tokenId: null, remainingUsd: null };
    wsLastEventIdRef.current = null;
    wsReconnectAttemptsRef.current = 0;
    expiredRefreshCooldownRef.current = 0;
    tokensFetchInFlightRef.current = false;
    tokensLastFetchedRef.current = 0;
    returnRedirectedRef.current = false;

    if (quoteRefreshTimeoutRef.current) {
      clearTimeout(quoteRefreshTimeoutRef.current);
      quoteRefreshTimeoutRef.current = null;
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (closeWebSocketRef.current) {
      closeWebSocketRef.current('session_change');
    }
  }, []);

  const syncSessionFromLocation = useCallback(() => {
    const currentSession = readSessionFromLocation();
    const prev = lastSessionRef.current;
    const changed = prev.token !== currentSession.token || prev.signature !== currentSession.signature;
    if (changed) {
      resetSessionState(currentSession);
    }
    return currentSession;
  }, [resetSessionState]);

  const getSessionHeaders = useCallback(() => {
    const currentSession = syncSessionFromLocation();
    if (sessionInvalidRef.current) return null;
    if (!currentSession.token || !currentSession.signature) {
      markSessionInvalid('unauthorized');
      return null;
    }
    return {
      'X-Session-Token': currentSession.token,
      'X-Signature': currentSession.signature,
      'Cache-Control': 'no-store',
      'Pragma': 'no-cache'
    };
  }, [markSessionInvalid, syncSessionFromLocation]);

  const handleAuthFailure = useCallback((response) => {
    if (!response) return false;
    if (response.status === 410 && response.data?.code === 'INVOICE_EXPIRED') {
      markSessionInvalid('expired');
      return true;
    }
    if (response.status === 401) {
      const errorCode = response.data?.code;
      if (errorCode === 'SESSION_EXPIRED') {
        markSessionInvalid('session_expired');
      } else if (errorCode === 'INVALID_SIGNATURE') {
        markSessionInvalid('invalid_signature');
      } else {
        markSessionInvalid('unauthorized');
      }
      return true;
    }
    return false;
  }, [markSessionInvalid]);

  useEffect(() => {
    const prev = lastSessionRef.current;
    const changed = prev.token !== sessionToken || prev.signature !== sessionSignature;
    if (!changed) return;

    resetSessionState({ token: sessionToken, signature: sessionSignature });
  }, [resetSessionState, sessionSignature, sessionToken]);

  useEffect(() => {
    returnRedirectedRef.current = false;
  }, [invoiceId, sessionSignature, sessionToken]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleContactClick = (event) => {
    setContactAnchorEl(event.currentTarget);
  };

  const handleContactClose = () => {
    setContactAnchorEl(null);
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(paymentDetails.merchant_email);
    handleContactClose();
    setSnackbar({
        open: true,
        message: 'Email copied to clipboard',
        severity: 'success'
    });
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${paymentDetails.merchant_email}`;
    handleContactClose();
  };

  const handleHelpClick = (event) => {
    setHelpAnchorEl(event.currentTarget);
  };

  const handleHelpClose = useCallback(() => {
    setHelpAnchorEl(null);
  }, []);

  const fetchSessionDetails = useCallback(async () => {
    const headers = getSessionHeaders();
    if (!headers) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/iframe/session-details`,
        {
          headers
        }
      );
      
      setPaymentDetails(response.data);
    } catch (error) {
      const response = error.response;
      // Handle different error types from middleware
      if (response?.status === 410 && response?.data?.code === 'INVOICE_EXPIRED') {
        setError('expired');
        markSessionInvalid('expired');
        setPaymentDetails(prev => ({
          ...prev,
          invoice_status: 'expired',
          invoice_id: response.data.invoice_id || prev?.invoice_id,
          expires_at: response.data.expires_at,
          expired_at: response.data.expired_at
        }));
      } else if (handleAuthFailure(response)) {
        return;
      } else {
        // Generic error - preserve existing behavior
        setError('Failed to load payment details. Please try again.');
      }
    }
  }, [getSessionHeaders, handleAuthFailure, markSessionInvalid]);

  const checkPaymentStatus = useCallback(async () => {
    const headers = getSessionHeaders();
    if (!headers) return null;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/iframe/payment-status`,
        {
          headers
        }
      );

      const normalizedStatus = String(response.data.status || response.data.invoice_status || '').toLowerCase();

      if (normalizedStatus === 'paid') {
        // Update payment details to reflect paid status
        setPaymentDetails(prev => ({
          ...prev,
          invoice_status: 'paid'
        }));
      } else if (normalizedStatus === 'underpaid' || normalizedStatus === 'partial') {
        await fetchSessionDetails();
      }

      return response.data;
    } catch (error) {
      if (handleAuthFailure(error.response)) {
        return null;
      }
      console.error('Failed to check payment status:', error);
      throw error;
    }
  }, [fetchSessionDetails, getSessionHeaders, handleAuthFailure]);

  const handleManualVerify = useCallback(async ({ networkOverride, source = 'manual' } = {}) => {
    try {
      if (source === 'manual') {
        setIsLoading(true);
      }
      const headers = getSessionHeaders();
      if (!headers) {
        return;
      }
      const verifyNetwork = networkOverride
        || txState.network
        || (isUnderpaid && lockedNetwork ? lockedNetwork : selectedNetwork)
        || lockedNetwork;
      if (!verifyNetwork) {
        throw new Error('Payment network not set');
      }

      setManualVerifyGateOpen(true);
      setCheckPaymentUi({ status: 'idle' });
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/invoice/${paymentDetails.invoice_id}/verify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...headers
          },
          body: JSON.stringify({
            network: verifyNetwork
          })
        }
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        if (handleAuthFailure({ status: response.status, data: errorBody })) {
          return;
        }
        throw new Error(errorBody.message || 'Verification failed');
      }

      const result = await response.json();
      if (result.transactions_processed > 0 || result?.data?.job_status === 'completed') {
        await checkPaymentStatus();
      }
      
      setSnackbar({
        open: true,
        message: source === 'auto_timeout'
          ? 'Verification requested. Checking payment status...'
          : 'Payment verification completed',
        severity: 'success'
      });
    } catch (error) {
      console.error('Manual verification failed:', error);
      setSnackbar({
        open: true,
        message: `Verification failed: ${error.message}`,
        severity: 'error'
      });
    } finally {
      if (source === 'manual') {
        setIsLoading(false);
        handleHelpClose();
      }
    }
  }, [
    checkPaymentStatus,
    getSessionHeaders,
    handleAuthFailure,
    handleHelpClose,
    isUnderpaid,
    lockedNetwork,
    paymentDetails?.invoice_id,
    selectedNetwork,
    txState.network
  ]);

  const handleTransactionSubmitted = (txDetails = {}) => {
    const hash = txDetails.hash || txDetails.txHash || txDetails.signature || txDetails.transactionHash || null;
    setTxState({
      status: 'submitted',
      hash,
      explorerUrl: txDetails.explorerUrl || null,
      network: txDetails.network || selectedNetwork || lockedNetwork || null,
      tokenSymbol: txDetails.tokenSymbol || selectedTokenInfo?.token_symbol || null,
      amount: txDetails.amount || tokenDetails?.estimatedAmount || null,
      error: null
    });
    setManualVerifyGateOpen(false);
    setCheckPaymentUi({ status: 'idle' });
    setSnackbar({
      open: true,
      message: 'Transaction submitted. Waiting for confirmation...',
      severity: 'info'
    });
  };

  const handleTransactionConfirmed = async (txDetails = {}) => {
    const hash = txDetails.hash || txDetails.txHash || txDetails.signature || txDetails.transactionHash || null;
    setTxState(prev => ({
      ...prev,
      status: 'confirmed',
      hash: hash || prev.hash,
      explorerUrl: txDetails.explorerUrl || prev.explorerUrl || null,
      error: null
    }));

    if (!hash || lastVerifiedTxRef.current === hash) {
      return;
    }
    if (verifyInFlightRef.current) {
      return;
    }

    lastVerifiedTxRef.current = hash;
    verifyInFlightRef.current = true;

    setSnackbar({
      open: true,
      message: 'Transaction confirmed. Verifying payment...',
      severity: 'success'
    });

    try {
      await handleManualVerify({ networkOverride: txDetails.network, source: 'auto_confirmed' });
    } finally {
      verifyInFlightRef.current = false;
    }
  };

  const handleTransactionFailed = (txDetails = {}) => {
    setTxState(prev => ({
      ...prev,
      status: 'failed',
      hash: txDetails.hash || txDetails.txHash || txDetails.signature || txDetails.transactionHash || prev.hash || null,
      explorerUrl: txDetails.explorerUrl || prev.explorerUrl || null,
      error: txDetails.errorMessage || txDetails.error || 'Transaction failed'
    }));
    handlePaymentError(txDetails.errorMessage || txDetails.error || 'Transaction failed');
  };

  useEffect(() => {
    manualVerifyRef.current = handleManualVerify;
  }, [handleManualVerify]);

  useEffect(() => {
    if (txFallbackTimerRef.current) {
      clearTimeout(txFallbackTimerRef.current);
      txFallbackTimerRef.current = null;
    }

    if (txState.status !== 'submitted' || !txState.hash || paymentDetails?.invoice_status === 'paid') {
      return;
    }

    if (txFallbackAttemptedRef.current.has(txState.hash)) {
      return;
    }

    txFallbackTimerRef.current = setTimeout(() => {
      if (verifyInFlightRef.current) {
        return;
      }
      if (txState.status !== 'submitted' || !txState.hash) {
        return;
      }
      if (txFallbackAttemptedRef.current.has(txState.hash)) {
        return;
      }
      txFallbackAttemptedRef.current.add(txState.hash);
      manualVerifyRef.current?.({ networkOverride: txState.network, source: 'auto_timeout' });
    }, 45000);

    return () => {
      if (txFallbackTimerRef.current) {
        clearTimeout(txFallbackTimerRef.current);
        txFallbackTimerRef.current = null;
      }
    };
  }, [paymentDetails?.invoice_status, txState.hash, txState.network, txState.status]);

  useEffect(() => {
    if (checkPaymentUi.status !== 'not_found') {
      if (checkPaymentCooldownRef.current) {
        clearTimeout(checkPaymentCooldownRef.current);
        checkPaymentCooldownRef.current = null;
      }
      return;
    }

    if (checkPaymentCooldownRef.current) {
      clearTimeout(checkPaymentCooldownRef.current);
    }

    checkPaymentCooldownRef.current = setTimeout(() => {
      setCheckPaymentUi(prev => (prev.status === 'not_found' ? { status: 'idle' } : prev));
    }, CHECK_PAYMENT_COOLDOWN_MS);

    return () => {
      if (checkPaymentCooldownRef.current) {
        clearTimeout(checkPaymentCooldownRef.current);
        checkPaymentCooldownRef.current = null;
      }
    };
  }, [checkPaymentUi.status]);

  const triggerPaymentReturn = useCallback((returnUrl) => {
    if (!returnUrl || returnRedirectedRef.current) {
      return;
    }
    let parsedUrl;
    try {
      parsedUrl = new URL(returnUrl);
    } catch (error) {
      return;
    }
    if (parsedUrl.protocol !== 'https:') {
      return;
    }

    returnRedirectedRef.current = true;
    const message = {
      type: 'pepay:payment:complete',
      return_url: returnUrl,
      invoice_id: invoiceId,
      session: sessionToken || null
    };

    try {
      window.parent?.postMessage(message, '*');
      window.opener?.postMessage?.(message, '*');
    } catch (error) {
      // Non-fatal: postMessage can fail on strict origins.
    }

    if (window.opener && !window.opener.closed) {
      try {
        window.opener.location.assign(returnUrl);
        window.close();
        return;
      } catch (error) {
        // Ignore and fall through to redirect.
      }
    }

    try {
      if (window.top && window.top !== window.self) {
        window.top.location.assign(returnUrl);
        return;
      }
    } catch (error) {
      // Cross-origin top access can throw.
    }

    window.location.assign(returnUrl);
  }, [invoiceId, sessionToken]);

  const handleCheckPayment = useCallback(async () => {
    if (!paymentDetails?.invoice_id) {
      return;
    }
    if (checkPaymentUi.status && checkPaymentUi.status !== 'idle') {
      return;
    }

    setCheckPaymentUi({ status: 'checking' });

    let isConfirmed = false;
    try {
      const statusData = await checkPaymentStatus();
      const normalizedStatus = String(statusData?.status || statusData?.invoice_status || '').toLowerCase();
      if (normalizedStatus === 'paid') {
        isConfirmed = true;
      }
    } catch (error) {
      console.error('Check payment status failed:', error);
    }

    if (!isConfirmed) {
      const headers = getSessionHeaders();
      if (!headers) {
        setCheckPaymentUi({ status: 'idle' });
        return;
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/v1/invoice/${paymentDetails.invoice_id}/verify`,
          {
            headers
          }
        );

        const job = response.data?.data;
        const outcome = String(job?.result?.outcome || '').toLowerCase();
        const invoiceStatus = String(job?.result?.invoice_status || '').toLowerCase();
        if (invoiceStatus === 'paid' || outcome === 'invoice_paid' || outcome === 'invoice_already_paid') {
          isConfirmed = true;
          await checkPaymentStatus();
        }
      } catch (error) {
        if (handleAuthFailure(error.response)) {
          return;
        }
        if (error.response?.status !== 404) {
          console.error('Manual verification status check failed:', error);
        }
      }
    }

    setCheckPaymentUi({ status: isConfirmed ? 'confirmed' : 'not_found' });
  }, [checkPaymentStatus, checkPaymentUi.status, getSessionHeaders, handleAuthFailure, paymentDetails?.invoice_id]);

  useEffect(() => {
    const normalizedStatus = String(paymentDetails?.invoice_status || '').toLowerCase();
    if (normalizedStatus !== 'paid') {
      return;
    }
    const returnUrl = paymentDetails?.payment_return_url;
    const isEnabled = paymentDetails?.payment_return_url_enabled !== false;
    if (!isEnabled || !returnUrl) {
      return;
    }
    triggerPaymentReturn(returnUrl);
  }, [
    paymentDetails?.invoice_status,
    paymentDetails?.payment_return_url,
    paymentDetails?.payment_return_url_enabled,
    triggerPaymentReturn
  ]);

  useEffect(() => {
    fetchSessionDetails();
  }, [fetchSessionDetails]);

  const fetchAvailableTokens = useCallback(async ({ force = false } = {}) => {
    if (tokensFetchInFlightRef.current) return;
    const now = Date.now();
    if (!force && tokensLastFetchedRef.current && (now - tokensLastFetchedRef.current) < TOKENS_REFETCH_COOLDOWN_MS) {
      return;
    }

    tokensFetchInFlightRef.current = true;
    const headers = getSessionHeaders();
    if (!headers) {
      tokensFetchInFlightRef.current = false;
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/iframe/available-tokens`,
        {
          params: {
            available_only: true
          },
          headers
        }
      );

      setAvailableTokens(response.data);
    } catch (err) {
      // Handle authentication errors the same way as fetchSessionDetails
      if (handleAuthFailure(err.response)) {
        return;
      } else {
        // Only set generic error if no authentication error was already set
        setError('Failed to fetch available payment options');
      }
    } finally {
      tokensFetchInFlightRef.current = false;
      tokensLastFetchedRef.current = Date.now();
    }
  }, [getSessionHeaders, handleAuthFailure]);

  useEffect(() => {
    if (!paymentDetails) return;
    const normalizedStatus = String(paymentDetails?.invoice_status || '').toLowerCase();
    if (normalizedStatus === 'paid' || normalizedStatus === 'expired') {
      return;
    }
    fetchAvailableTokens({ force: true });
  }, [fetchAvailableTokens, paymentDetails?.invoice_status, paymentDetails]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchAvailableTokens();
      }
    };

    const handleFocus = () => {
      fetchAvailableTokens();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchAvailableTokens]);

  const handleNetworkSelect = (network) => {
    if (isUnderpaid) {
      return;
    }
    setSelectedNetwork(network);
    setSelectedToken(null);
  };

  const resolveQuoteValidUntilMs = useCallback((quoteValidUntil, quoteExpiresInSeconds) => {
    const parsed = toEpochMs(quoteValidUntil);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
    const expiresSeconds = quoteExpiresInSeconds !== null && quoteExpiresInSeconds !== undefined
      ? Number(quoteExpiresInSeconds)
      : null;
    if (Number.isFinite(expiresSeconds)) {
      return Date.now() + expiresSeconds * 1000;
    }
    return null;
  }, []);

  const clearWsTimers = useCallback(() => {
    if (wsIdleTimeoutRef.current) {
      clearTimeout(wsIdleTimeoutRef.current);
      wsIdleTimeoutRef.current = null;
    }
    if (wsMaxLifetimeTimeoutRef.current) {
      clearTimeout(wsMaxLifetimeTimeoutRef.current);
      wsMaxLifetimeTimeoutRef.current = null;
    }
  }, []);

  const clearWsReconnectTimeout = useCallback(() => {
    if (wsReconnectTimeoutRef.current) {
      clearTimeout(wsReconnectTimeoutRef.current);
      wsReconnectTimeoutRef.current = null;
    }
  }, []);

  const closeWebSocket = useCallback((reason) => {
    wsManualCloseRef.current = true;
    clearWsReconnectTimeout();
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      wsRef.current.onopen = null;
      try {
        wsRef.current.close(1000, reason || 'closed');
      } catch (_e) {}
    }
    wsRef.current = null;
    wsConnectionDetailsRef.current = null;
    wsReconnectAttemptsRef.current = 0;
    clearWsTimers();
  }, [clearWsReconnectTimeout, clearWsTimers]);

  useEffect(() => {
    closeWebSocketRef.current = closeWebSocket;
  }, [closeWebSocket]);

  const bumpWsIdleTimeout = useCallback(() => {
    if (wsIdleTimeoutRef.current) {
      clearTimeout(wsIdleTimeoutRef.current);
    }
    wsIdleTimeoutRef.current = setTimeout(() => {
      closeWebSocket('idle_timeout');
      setPollingEnabled(true);
    }, WS_IDLE_TIMEOUT_MS);
  }, [closeWebSocket]);

  const startWsTimers = useCallback(() => {
    clearWsTimers();
    bumpWsIdleTimeout();
    wsMaxLifetimeTimeoutRef.current = setTimeout(() => {
      closeWebSocket('max_lifetime');
      setPollingEnabled(true);
    }, WS_MAX_LIFETIME_MS);
  }, [bumpWsIdleTimeout, clearWsTimers, closeWebSocket]);

  const appendQueryParam = useCallback((endpoint, key, value) => {
    if (!value) return endpoint;
    const separator = endpoint.includes('?') ? '&' : '?';
    return `${endpoint}${separator}${key}=${encodeURIComponent(value)}`;
  }, []);

  const scheduleQuoteRefresh = useCallback((validUntilMs, retryAfterSeconds) => {
    if (isQuoteFrozen) return;
    if (quoteRefreshTimeoutRef.current) {
      clearTimeout(quoteRefreshTimeoutRef.current);
    }
    const now = Date.now();
    const retrySeconds = retryAfterSeconds !== null && retryAfterSeconds !== undefined
      ? Number(retryAfterSeconds)
      : null;
    let delayMs = null;
    if (Number.isFinite(retrySeconds) && retrySeconds > 0) {
      delayMs = retrySeconds * 1000;
    } else if (Number.isFinite(validUntilMs)) {
      delayMs = Math.max(0, validUntilMs - now + QUOTE_REFRESH_AFTER_EXPIRY_MS);
    }
    if (delayMs === null) return;
    quoteRefreshTimeoutRef.current = setTimeout(() => {
      refreshQuoteRef.current?.({ reason: 'scheduled' });
    }, delayMs);
  }, [isQuoteFrozen]);

  const applyQuoteUpdate = useCallback((quoteUpdate) => {
    if (!quoteUpdate) return;
    if (isQuoteFrozen) return;
    const validUntilMs = resolveQuoteValidUntilMs(
      quoteUpdate.quoteValidUntil,
      quoteUpdate.quoteExpiresInSeconds
    );
    const expiresAtMs = Number.isFinite(validUntilMs) ? validUntilMs : null;

    setTokenDetails(prev => ({
      ...prev,
      estimatedAmount: quoteUpdate.estimatedTokenAmount ?? prev?.estimatedAmount ?? null,
      exchangeRate: quoteUpdate.exchangeRate ?? prev?.exchangeRate ?? null,
      network: quoteUpdate.network ?? prev?.network,
      tokenId: quoteUpdate.tokenId ?? prev?.tokenId,
      quoteStatus: quoteUpdate.quoteStatus ?? prev?.quoteStatus,
      retryable: quoteUpdate.retryable ?? prev?.retryable,
      retryAfterSeconds: quoteUpdate.retryAfterSeconds ?? prev?.retryAfterSeconds,
      quoteValidUntil: quoteUpdate.quoteValidUntil ?? prev?.quoteValidUntil,
      quoteExpiresInSeconds: quoteUpdate.quoteExpiresInSeconds ?? prev?.quoteExpiresInSeconds,
      expiresAt: expiresAtMs ?? prev?.expiresAt
    }));

    if (Number.isFinite(validUntilMs)) {
      setPriceValidUntil(validUntilMs);
      setIsQuoteExpired(Date.now() >= validUntilMs);
    } else if (quoteUpdate.quoteStatus === 'expired') {
      setIsQuoteExpired(true);
    }

    if (!isQuoteFrozen) {
      scheduleQuoteRefresh(validUntilMs, quoteUpdate.retryable ? quoteUpdate.retryAfterSeconds : null);
    }
  }, [isQuoteFrozen, resolveQuoteValidUntilMs, scheduleQuoteRefresh]);

  const handleWebSocketMessage = useCallback((message) => {
    if (sessionInvalidRef.current) return;
    if (!message || typeof message !== 'object') return;

    if (message.object === 'ws_error') {
      setPollingEnabled(true);
      closeWebSocket('ws_error');
      return;
    }

    if (message.object === 'ws_control') {
      if (message.type === 'replay_truncated') {
        wsLastEventIdRef.current = null;
        checkPaymentStatus().catch(error => {
          console.error('Payment status refresh failed after replay truncation:', error);
        });
        fetchSessionDetails().catch(error => {
          console.error('Session refresh failed after replay truncation:', error);
        });
        if (!isQuoteFrozenRef.current) {
          refreshQuoteRef.current?.({ reason: 'ws_replay_truncated' });
        }
      }
      return;
    }

    if (message.type === 'payment_status' || message.type === 'payment_partial') {
      const normalizedStatus = String(message.data?.status || '').toLowerCase();
      if (normalizedStatus === 'paid') {
        setPaymentDetails(prev => ({
          ...prev,
          invoice_status: 'paid'
        }));
      } else if (normalizedStatus === 'underpaid' || normalizedStatus === 'partial') {
        fetchSessionDetails().catch(error => {
          console.error('Failed to refresh session after partial payment event:', error);
        });
      }
      return;
    }

    if (message.object !== 'event') {
      return;
    }

    if (typeof message.id === 'string' && message.id.length > 0) {
      wsLastEventIdRef.current = message.id;
    }

    const eventType = message.type;
    const dataObject = message.data?.object;
    if (!eventType || !dataObject) return;

    if (eventType === 'payment.quote.updated' && dataObject.object === 'payment_quote') {
      if (isQuoteFrozen) return;
      applyQuoteUpdate({
        estimatedTokenAmount: dataObject.estimated_token_amount_remaining,
        exchangeRate: dataObject.exchange_rate,
        network: dataObject.network,
        tokenId: dataObject.supported_token_id,
        quoteValidUntil: dataObject.quote_valid_until,
        quoteExpiresInSeconds: dataObject.quote_expires_in_seconds,
        quoteStatus: dataObject.quote_status,
        retryable: dataObject.retryable,
        retryAfterSeconds: dataObject.retry_after_seconds
      });
      return;
    }

    if (eventType === 'invoice.updated' && dataObject.object === 'invoice') {
      if (dataObject.status) {
        setPaymentDetails(prev => ({
          ...prev,
          invoice_status: dataObject.status,
          expires_at: dataObject.expires_at || prev?.expires_at,
          payment_return_url: dataObject.payment_return_url || prev?.payment_return_url
        }));
      }
      const normalizedStatus = String(dataObject.status || '').toLowerCase();
      if (normalizedStatus === 'underpaid' || normalizedStatus === 'partial') {
        fetchSessionDetails().catch(error => {
          console.error('Failed to refresh session after invoice update:', error);
        });
      }
      return;
    }

    if (eventType.startsWith('invoice_payment.')) {
      checkPaymentStatus().catch(error => {
        console.error('Payment status refresh failed after websocket event:', error);
      });
    }
  }, [applyQuoteUpdate, checkPaymentStatus, closeWebSocket, fetchSessionDetails, isQuoteFrozen]);

  const scheduleWsReconnect = useCallback((reason) => {
    if (sessionInvalidRef.current) return;
    if (wsManualCloseRef.current) return;
    if (isQuoteFrozenRef.current) return;
    const wsDetails = wsConnectionDetailsRef.current;
    if (!wsDetails?.token) {
      setPollingEnabled(true);
      return;
    }
    if (wsReconnectAttemptsRef.current >= WS_RECONNECT_MAX_ATTEMPTS) {
      setPollingEnabled(true);
      return;
    }
    const attempt = wsReconnectAttemptsRef.current + 1;
    wsReconnectAttemptsRef.current = attempt;
    const baseDelay = WS_RECONNECT_BASE_DELAY_MS * 2 ** (attempt - 1);
    const jitter = Math.floor(Math.random() * 250);
    const delay = Math.min(WS_RECONNECT_MAX_DELAY_MS, baseDelay + jitter);
    clearWsReconnectTimeout();
    wsReconnectTimeoutRef.current = setTimeout(() => {
      if (connectWebSocketRef.current) {
        connectWebSocketRef.current(wsDetails, { isReconnect: true, reason });
      }
    }, delay);
  }, [clearWsReconnectTimeout]);

  const connectWebSocket = useCallback((wsDetails, options = {}) => {
    if (sessionInvalidRef.current) return null;
    if (!wsDetails?.token) return null;

    closeWebSocket('reconnect');
    wsManualCloseRef.current = false;
    wsConnectionDetailsRef.current = wsDetails;
    if (!options.isReconnect) {
      wsReconnectAttemptsRef.current = 0;
    }
    clearWsReconnectTimeout();

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_WS_URL.replace(/^(ws|wss):\/\//, '');
    let endpoint = wsDetails.ws_url || wsDetails.endpoint || '/ws/payment';
    if (!wsDetails.ws_url) {
      const format = wsDetails.format || 'event_v1';
      endpoint = appendQueryParam(endpoint, 'format', format);
    }
    if (!/[?&]token=/.test(endpoint)) {
      endpoint = appendQueryParam(endpoint, 'token', wsDetails.token);
    }
    if (options.isReconnect && wsLastEventIdRef.current && !/[?&]since=/.test(endpoint)) {
      endpoint = appendQueryParam(endpoint, 'since', wsLastEventIdRef.current);
    }
    const wsUrl = `${protocol}//${wsHost}${endpoint}`;

    const logUrl = wsUrl.replace(/([?&]token=)[^&]+/, '$1REDACTED');
    debugLog('Attempting WebSocket connection to:', logUrl);

    const websocket = new WebSocket(wsUrl);
    wsRef.current = websocket;

    websocket.onopen = () => {
      if (sessionInvalidRef.current) {
        closeWebSocket('session_invalid');
        return;
      }
      wsReconnectAttemptsRef.current = 0;
      setPollingEnabled(false);
      startWsTimers();
    };

    websocket.onerror = (error) => {
      if (sessionInvalidRef.current) {
        return;
      }
      console.error('WebSocket error:', error);
      setPollingEnabled(true);
      scheduleWsReconnect('error');
    };

    websocket.onmessage = (event) => {
      bumpWsIdleTimeout();
      try {
        const message = JSON.parse(event.data);
        handleWebSocketMessage(message);
      } catch (error) {
        console.error('Error handling WebSocket message:', error);
      }
    };

    websocket.onclose = (event) => {
      clearWsTimers();
      wsRef.current = null;

      if (sessionInvalidRef.current) {
        return;
      }

      const closeCode = event?.code;
      const closeReason = typeof event?.reason === 'string' ? event.reason : '';

      if (closeCode === 4004 || /expired session/i.test(closeReason)) {
        markSessionInvalid('session_expired');
        return;
      }

      if (closeCode === 4001 && wsLastEventIdRef.current) {
        wsLastEventIdRef.current = null;
        scheduleWsReconnect('invalid_since');
        return;
      }

      if (closeCode === 4001 && /invalid session/i.test(closeReason)) {
        markSessionInvalid('unauthorized');
        return;
      }

      setPollingEnabled(true);
      if (closeCode === 4001 || closeCode === 4002 || closeCode === 4003 || closeCode === 4005) {
        return;
      }
      scheduleWsReconnect('close');
    };

    return websocket;
  }, [
    appendQueryParam,
    bumpWsIdleTimeout,
    clearWsReconnectTimeout,
    clearWsTimers,
    closeWebSocket,
    handleWebSocketMessage,
    markSessionInvalid,
    scheduleWsReconnect,
    startWsTimers
  ]);

  useEffect(() => {
    connectWebSocketRef.current = connectWebSocket;
  }, [connectWebSocket]);

  const requestPaymentAddress = useCallback(async (network, tokenId) => {
    const headers = getSessionHeaders();
    if (!headers) return null;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/iframe/payment-addresses`,
        {
          network: network,
          tokenId: tokenId
        },
        {
          headers
        }
      );

      return response.data;
    } catch (error) {
      if (handleAuthFailure(error.response)) {
        return null;
      }
      throw error;
    }
  }, [getSessionHeaders, handleAuthFailure]);

  const resetTxState = useCallback(() => {
    setTxState({
      status: 'idle',
      hash: null,
      explorerUrl: null,
      network: null,
      tokenSymbol: null,
      amount: null,
      error: null
    });
    setManualVerifyGateOpen(false);
    setCheckPaymentUi({ status: 'idle' });
    lastVerifiedTxRef.current = null;
  }, []);

  const handlePaymentAddressCreated = useCallback((response) => {
    if (!response) return;

    if (response.temp_address) {
      setPaymentAddress(response.temp_address);
    }

    if (!isQuoteFrozenRef.current) {
      resetTxState();
    }

    applyQuoteUpdate({
      estimatedTokenAmount: response.estimated_token_amount,
      exchangeRate: response.exchange_rate,
      network: response.network,
      tokenId: response.supported_token_id,
      quoteValidUntil: response.quote_valid_until,
      quoteExpiresInSeconds: response.quote_expires_in_seconds,
      quoteStatus: response.quote_status,
      retryable: response.retryable,
      retryAfterSeconds: response.retry_after_seconds
    });

    if (response.ws_connection && !isQuoteFrozen) {
      connectWebSocket(response.ws_connection);
    } else {
      setPollingEnabled(true);
    }
  }, [applyQuoteUpdate, connectWebSocket, resetTxState, isQuoteFrozen]);

  const refreshQuote = useCallback(async ({ reason: _reason } = {}) => {
    if (!selectedNetwork || !selectedToken) return;
    if (isQuoteFrozen) return;
    if (refreshInFlightRef.current) return;

    refreshInFlightRef.current = true;
    setIsRefreshingQuote(true);

    try {
      const data = await requestPaymentAddress(selectedNetwork, selectedToken);
      if (isQuoteFrozenRef.current) {
        return;
      }
      handlePaymentAddressCreated(data);
    } catch (error) {
      console.error('Error refreshing payment quote:', error);
    } finally {
      refreshInFlightRef.current = false;
      setIsRefreshingQuote(false);
    }
  }, [handlePaymentAddressCreated, isQuoteFrozen, requestPaymentAddress, selectedNetwork, selectedToken]);

  useEffect(() => {
    refreshQuoteRef.current = refreshQuote;
  }, [refreshQuote]);

  useEffect(() => {
    isQuoteFrozenRef.current = isQuoteFrozen;
  }, [isQuoteFrozen]);

  const handlePaymentInitiated = useCallback(() => {
    if (isQuoteFrozen) return;
    setIsQuoteFrozen(true);
    setPollingEnabled(true);
    if (quoteRefreshTimeoutRef.current) {
      clearTimeout(quoteRefreshTimeoutRef.current);
      quoteRefreshTimeoutRef.current = null;
    }
    closeWebSocket('payment_started');
  }, [closeWebSocket, isQuoteFrozen]);

  const handlePaymentError = useCallback((message) => {
    setIsQuoteFrozen(false);
    setPollingEnabled(false);
    setManualVerifyGateOpen(false);
    setCheckPaymentUi({ status: 'idle' });
    setSnackbar({
      open: true,
      message,
      severity: 'error'
    });
    setTimeout(() => {
      refreshQuoteRef.current?.({ reason: 'payment_failed' });
    }, 1000);
  }, []);

  const handleTokenSelect = async (tokenId) => {
    if (isUnderpaid) {
      return;
    }
    const normalizedTokenId = normalizeTokenId(tokenId);
    setSelectedToken(normalizedTokenId);
    if (!normalizedTokenId || !selectedNetwork) return;

    setIsQuoteFrozen(false);
    setPollingEnabled(false);
    setIsQuoteExpired(false);
    if (quoteRefreshTimeoutRef.current) {
      clearTimeout(quoteRefreshTimeoutRef.current);
      quoteRefreshTimeoutRef.current = null;
    }
    closeWebSocket('new_token');

    refreshInFlightRef.current = true;
    setIsRefreshingQuote(true);

    try {
      const data = await requestPaymentAddress(selectedNetwork, normalizedTokenId);
      handlePaymentAddressCreated(data);
    } catch (error) {
      console.error('Error generating payment address:', error);
    } finally {
      refreshInFlightRef.current = false;
      setIsRefreshingQuote(false);
    }
  };

  useEffect(() => {
    if (!isUnderpaid) {
      partialLockRef.current = { network: null, tokenId: null, remainingUsd: null };
      return;
    }

    if (!lockedNetwork || !lockedTokenId) {
      return;
    }

    const normalizedNetwork = String(lockedNetwork).toLowerCase();
    const normalizedTokenId = Number(lockedTokenId);
    if (!normalizedNetwork || !Number.isFinite(normalizedTokenId)) {
      return;
    }

    if (selectedNetwork !== normalizedNetwork) {
      setSelectedNetwork(normalizedNetwork);
    }
    if (Number(selectedToken) !== normalizedTokenId) {
      setSelectedToken(normalizedTokenId);
    }

    const remainingUsdValue = Number(
      paymentDetails?.amount_remaining_usd ?? paymentDetails?.amount_usd ?? NaN
    );
    const normalizedRemainingUsd = Number.isFinite(remainingUsdValue) ? remainingUsdValue : null;

    const alreadyRequested = partialLockRef.current.network === normalizedNetwork &&
      partialLockRef.current.tokenId === normalizedTokenId &&
      partialLockRef.current.remainingUsd === normalizedRemainingUsd &&
      paymentAddress;

    if (alreadyRequested || refreshInFlightRef.current) {
      return;
    }

    partialLockRef.current = {
      network: normalizedNetwork,
      tokenId: normalizedTokenId,
      remainingUsd: normalizedRemainingUsd
    };

    setIsQuoteFrozen(false);
    setIsQuoteExpired(false);
    setPollingEnabled(false);
    if (quoteRefreshTimeoutRef.current) {
      clearTimeout(quoteRefreshTimeoutRef.current);
      quoteRefreshTimeoutRef.current = null;
    }
    closeWebSocket('partial_lock');

    refreshInFlightRef.current = true;
    setIsRefreshingQuote(true);

    requestPaymentAddress(normalizedNetwork, normalizedTokenId)
      .then((data) => {
        if (isQuoteFrozenRef.current) return;
        handlePaymentAddressCreated(data);
      })
      .catch((error) => {
        console.error('Error generating payment address for partial payment:', error);
      })
      .finally(() => {
        refreshInFlightRef.current = false;
        setIsRefreshingQuote(false);
      });
  }, [
    closeWebSocket,
    handlePaymentAddressCreated,
    isUnderpaid,
    lockedNetwork,
    lockedTokenId,
    paymentAddress,
    paymentDetails?.amount_remaining_usd,
    paymentDetails?.amount_usd,
    requestPaymentAddress,
    selectedNetwork,
    selectedToken
  ]);

  useEffect(() => {
    if (!priceValidUntil) return;

    const timer = setInterval(() => {
      const now = Date.now();
      const distance = priceValidUntil - now;

      if (distance <= 0) {
        setCountdown({ minutes: 0, seconds: 0, isExpired: true });
        setIsQuoteExpired(true);
        clearInterval(timer);
        return;
      }

      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      const isExpiringSoon = distance <= 2 * 60 * 1000;
      setCountdown({ minutes, seconds, isExpiringSoon, isExpired: false });
      if (isQuoteExpired) {
        setIsQuoteExpired(false);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isQuoteExpired, priceValidUntil]);

  useEffect(() => {
    if (!isQuoteExpired) {
      expiredRefreshCooldownRef.current = 0;
      return;
    }
    if (isQuoteFrozen || isRefreshingQuote) return;
    if (!refreshQuoteRef.current) return;
    if (refreshInFlightRef.current) return;

    const retrySeconds = tokenDetails?.retryable
      ? Number(tokenDetails.retryAfterSeconds)
      : null;
    const delayMs = Number.isFinite(retrySeconds) && retrySeconds > 0
      ? retrySeconds * 1000
      : QUOTE_REFRESH_AFTER_EXPIRY_MS;
    const now = Date.now();

    if (expiredRefreshCooldownRef.current && now < expiredRefreshCooldownRef.current) {
      return;
    }

    expiredRefreshCooldownRef.current = now + delayMs;

    const timer = setTimeout(() => {
      if (!isQuoteFrozenRef.current) {
        refreshQuoteRef.current?.({ reason: 'expired_auto' });
      }
    }, delayMs);

    return () => clearTimeout(timer);
  }, [
    isQuoteExpired,
    isQuoteFrozen,
    isRefreshingQuote,
    tokenDetails?.retryable,
    tokenDetails?.retryAfterSeconds
  ]);

  useEffect(() => {
    if (!paymentDetails) return;
    const normalizedStatus = String(paymentDetails?.invoice_status || '').toLowerCase();
    if (normalizedStatus === 'paid' || normalizedStatus === 'expired') {
      return;
    }
    const fetchQuestions = async () => {
      const headers = getSessionHeaders();
      if (!headers) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/v1/iframe/invoice-questions`,
          {
            headers
          }
        );
        
        if (response.data.has_questions) {
          setQuestions(response.data.questions);
          setShowForm(true);
        }
      } catch (error) {
        // Handle authentication errors the same way as other endpoints
        if (handleAuthFailure(error.response)) {
          return;
        }
        // For questions, we don't set a generic error since it's optional
      }
    };
    
    fetchQuestions();
  }, [getSessionHeaders, handleAuthFailure, paymentDetails, paymentDetails?.invoice_status]);

  // Add the payment status polling useEffect here
  useEffect(() => {
    if (!pollingEnabled || !paymentAddress || paymentDetails?.invoice_status === 'paid') {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    checkPaymentStatus().catch(error => {
      console.error('Initial payment status check failed:', error);
    });

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const statusData = await checkPaymentStatus();
        if (!statusData) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          return;
        }
        if (statusData.status === 'paid' || statusData.invoice_status === 'paid') {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } catch (error) {
        console.error('Payment status polling failed:', error);
        if (error.response?.status === 410 || error.response?.status === 401) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    }, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [checkPaymentStatus, paymentAddress, paymentDetails?.invoice_status, pollingEnabled]);

  useEffect(() => {
    return () => {
      if (quoteRefreshTimeoutRef.current) {
        clearTimeout(quoteRefreshTimeoutRef.current);
        quoteRefreshTimeoutRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      closeWebSocket('unmount');
    };
  }, [closeWebSocket]);

  useEffect(() => {
    if (paymentDetails?.invoice_status !== 'paid') return;
    setPollingEnabled(false);
    if (quoteRefreshTimeoutRef.current) {
      clearTimeout(quoteRefreshTimeoutRef.current);
      quoteRefreshTimeoutRef.current = null;
    }
    closeWebSocket('paid');
  }, [closeWebSocket, paymentDetails?.invoice_status]);

  const handleFormSubmit = async (formData) => {
    // Here you would typically send the form data to your backend
    setFormSubmitted(true);
    setShowForm(false);
  };

  const handleFormSkip = () => {
    setFormSubmitted(true);
    setShowForm(false);
  };

  // Add handler for card expansion
  const handleCardToggle = (cardType) => {
    if (cardType === 'quick') {
      setIsQuickPayExpanded(!isQuickPayExpanded);
      if (!isQuickPayExpanded) {
        setIsManualPayExpanded(false);
      }
    } else {
      setIsManualPayExpanded(!isManualPayExpanded);
      if (!isManualPayExpanded) {
        setIsQuickPayExpanded(false);
      }
    }
  };

  const selectedTokenId = normalizeTokenId(selectedToken);
  const selectedTokenInfo = selectedTokenId
    ? availableTokens.find(t => Number(t.id) === selectedTokenId)
    : null;
  const selectedTokenLogoUri = selectedTokenInfo?.token_uri || selectedTokenInfo?.photo_uri || null;
  const lockedTokenIdValue = normalizeTokenId(lockedTokenId);
  const lockedTokenInfo = lockedTokenIdValue
    ? availableTokens.find(t => Number(t.id) === lockedTokenIdValue)
    : null;
  const underpaidTokenSymbol = lockedTokenInfo?.token_symbol || selectedTokenInfo?.token_symbol || 'this token';
  const underpaidNetworkName = lockedNetwork
    ? getNetworkDisplayName(lockedNetwork)
    : selectedNetwork
      ? getNetworkDisplayName(selectedNetwork)
      : 'this network';
  const remainingUsd = paymentDetails?.amount_remaining_usd ?? paymentDetails?.amount_usd ?? null;
  const formattedRemainingUsd = Number.isFinite(Number(remainingUsd))
    ? Number(remainingUsd).toFixed(2)
    : null;
  const isPaymentDisabled = !tokenDetails?.estimatedAmount ||
    tokenDetails?.quoteStatus !== 'available' ||
    isQuoteExpired ||
    isRefreshingQuote ||
    isQuoteFrozen;

  const handleSnackbarClose = (_event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  let content = null;

  if (error) {
    content = (
      <StyledCard>
        <ContentWrapper>
          {/* Generic error (preserve existing behavior) */}
          {typeof error === 'string' && error !== 'expired' && error !== 'session_expired' && error !== 'invalid_signature' && error !== 'unauthorized' && (
            <Alert severity="error">{error}</Alert>
          )}
          
          {/* Specific error types */}
          {(error === 'expired' || error === 'session_expired' || error === 'invalid_signature' || error === 'unauthorized') && (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center"
              sx={{ 
                minHeight: '300px',
                padding: '2rem',
                textAlign: 'center'
              }}
            >
              {/* Expired Invoice */}
              {error === 'expired' && (
                <>
                  <Box sx={{ marginBottom: '1rem' }}>
                    <ScheduleIcon 
                      color="error" 
                      sx={{ 
                        fontSize: 72,
                        filter: 'drop-shadow(0px 4px 8px rgba(244, 67, 54, 0.3))'
                      }} 
                    />
                  </Box>
                  <Typography variant="h5" color="error" gutterBottom>
                    Payment Link Expired
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    This payment link is no longer valid. Please contact the merchant to request a new payment link.
                  </Typography>
                  {paymentDetails?.expired_at && (
                    <Typography variant="body2" color="text.disabled" gutterBottom>
                      Expired: {formatDateTime(paymentDetails.expired_at)}
                    </Typography>
                  )}
                  {paymentDetails?.invoice_id && (
                    <Typography variant="body2" color="text.disabled">
                      Invoice ID: {paymentDetails.invoice_id}
                    </Typography>
                  )}
                </>
              )}
              
              {/* Session Expired */}
              {error === 'session_expired' && (
                <>
                  <Box sx={{ marginBottom: '1rem' }}>
                    <ScheduleIcon 
                      color="warning" 
                      sx={{ 
                        fontSize: 72,
                        filter: 'drop-shadow(0px 4px 8px rgba(255, 152, 0, 0.3))'
                      }} 
                    />
                  </Box>
                  <Typography variant="h5" color="warning.main" gutterBottom>
                    Session Expired
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    Your payment session has expired. Please refresh the page or request a new payment link from the merchant.
                  </Typography>
                  <Button 
                    variant="contained" 
                    onClick={() => window.location.reload()}
                    sx={{ mt: 2 }}
                  >
                    Refresh Page
                  </Button>
                </>
              )}
              
              {/* Invalid Signature */}
              {error === 'invalid_signature' && (
                <>
                  <Box sx={{ marginBottom: '1rem' }}>
                    <WarningIcon 
                      color="error" 
                      sx={{ 
                        fontSize: 72,
                        filter: 'drop-shadow(0px 4px 8px rgba(244, 67, 54, 0.3))'
                      }} 
                    />
                  </Box>
                  <Typography variant="h5" color="error" gutterBottom>
                    Invalid Payment Link
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    This payment link appears to be invalid or tampered with. Please request a new payment link from the merchant.
                  </Typography>
                </>
              )}
              
              {/* Unauthorized */}
              {error === 'unauthorized' && (
                <>
                  <Box sx={{ marginBottom: '1rem' }}>
                    <WarningIcon 
                      color="error" 
                      sx={{ 
                        fontSize: 72,
                        filter: 'drop-shadow(0px 4px 8px rgba(244, 67, 54, 0.3))'
                      }} 
                    />
                  </Box>
                  <Typography variant="h5" color="error" gutterBottom>
                    Access Denied
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    You don't have permission to access this payment link. Please contact the merchant for assistance.
                  </Typography>
                </>
              )}
            </Box>
          )}
        </ContentWrapper>
      </StyledCard>
    );
  } else if (!paymentDetails) {
    content = (
      <StyledCard>
        <ContentWrapper sx={{ alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography>Loading payment details...</Typography>
        </ContentWrapper>
      </StyledCard>
    );
  } else if (paymentDetails?.invoice_status === 'expired') {
    content = (
      <StyledCard>
        <ContentWrapper>
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center"
            sx={{ 
              minHeight: '300px',
              padding: '2rem',
              textAlign: 'center'
            }}
          >
            <Box sx={{ marginBottom: '1rem' }}>
              <ScheduleIcon 
                color="error" 
                sx={{ 
                  fontSize: 72,
                  filter: 'drop-shadow(0px 4px 8px rgba(244, 67, 54, 0.3))'
                }} 
              />
            </Box>
            <Typography variant="h5" color="error" gutterBottom>
              Payment Link Expired
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              This payment link is no longer valid. Please contact the merchant to request a new payment link.
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Invoice ID: {paymentDetails.invoice_id}
            </Typography>
          </Box>
        </ContentWrapper>
      </StyledCard>
    );
  } else {
    content = (
      <>
        {showFAQs ? (
          <FAQs onBack={() => setShowFAQs(false)} />
        ) : showPayments ? (
          <ViewPayments onBack={() => setShowPayments(false)} />
        ) : (
          <StyledCard>
          <ContentWrapper>
            <LogoWrapper>
              <img src="/pepay-logo-rounded-official.png" alt="PePay Logo" />
            </LogoWrapper>
            <Box sx={{ position: 'absolute', top: '16px', right: '16px', display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedNetwork === 'bsc' && walletInfo.isConnected && walletInfo.address && (
                <WalletStatusChip
                  isConnected={walletInfo.isConnected}
                  address={walletInfo.address}
                  networkName={selectedNetwork === 'bsc' ? 
                    (paymentDetails?.network_environment === 'mainnet' ? 'BNB Chain' : 'BNB Chain Testnet') : 'Unknown'}
                  tokenSymbol={selectedTokenInfo?.token_symbol}
                  isTestnet={paymentDetails?.network_environment !== 'mainnet'}
                  walletType={walletInfo.walletType || 'Wallet'}
                  balance={walletInfo.balance}
                />
              )}
              
              <IconButton
                aria-label="more"
                aria-controls={open ? 'long-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
            <Menu
              id="long-menu"
              MenuListProps={{
                'aria-labelledby': 'long-button',
              }}
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                style: {
                  width: '20ch',
                },
              }}
            >
              <MenuItem onClick={() => {
                setShowPayments(true);
                handleClose();
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentsIcon fontSize="small" />
                  <span>View Payments</span>
                </Box>
              </MenuItem>
              <MenuItem onClick={() => {
                setShowFAQs(true);
                handleClose();
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ContactSupportIcon fontSize="small" />
                  <span>FAQs</span>
                </Box>
              </MenuItem>
              <MenuItem onClick={handleContactClick}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon fontSize="small" />
                  <span>Contact</span>
                </Box>
              </MenuItem>
            </Menu>
            <Menu
              id="contact-menu"
              anchorEl={contactAnchorEl}
              open={contactOpen}
              onClose={handleContactClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <MenuItem onClick={handleEmailClick}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon fontSize="small" />
                  <span>Send Email</span>
                </Box>
              </MenuItem>
              <MenuItem onClick={handleCopyEmail}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ContentCopyIcon fontSize="small" />
                  <span>Copy Email</span>
                </Box>
              </MenuItem>
            </Menu>
            <CompanyHeader>
              <Typography className="company-name">
                {paymentDetails.merchant_name || 'Apple, Inc.'}
              </Typography>
              <Typography className="transaction-id">
                <Box component="span" className="label" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                  Transaction ID: 
                </Box>
                <Box component="span" className="id" sx={{ display: { xs: 'block', sm: 'inline' }, ml: { xs: 0, sm: 0.5 } }}>
                  {paymentDetails.invoice_id ? paymentDetails.invoice_id.split('-').slice(0, 4).join('-') : 'TXN-123456789'}
                </Box>
              </Typography>
            </CompanyHeader>

            {showForm && !formSubmitted ? (
              <FormComponent 
                questions={questions}
                onSubmit={handleFormSubmit}
                onSkip={handleFormSkip}
                sessionToken={sessionToken}
                sessionSignature={sessionSignature}
                onAuthFailure={handleAuthFailure}
              />
            ) : paymentDetails.invoice_status === 'paid' ? (
              <Fade in>
                <Box 
                  display="flex" 
                  flexDirection="column" 
                  alignItems="center" 
                  justifyContent="center"
                  sx={{ 
                    minHeight: '300px',
                    padding: '2rem',
                    textAlign: 'center'
                  }}
                >
                  <Box
                    sx={{
                      animation: 'spin-and-scale 3s ease-out',
                      '@keyframes spin-and-scale': {
                        '0%': {
                          transform: 'scale(0.5) rotate(0deg)',
                          opacity: 0
                        },
                        '50%': {
                          transform: 'scale(1.2) rotate(360deg)',
                          opacity: 0.7
                        },
                        '100%': {
                          transform: 'scale(1) rotate(720deg)',
                          opacity: 1
                        }
                      }
                    }}
                  >
                    <CheckCircleIcon 
                      color="success" 
                      sx={{ 
                        fontSize: 96,
                        filter: 'drop-shadow(0px 4px 8px rgba(76, 175, 80, 0.3))'
                      }} 
                    />
                  </Box>
                  <Typography 
                    variant="h5" 
                    color="success.main" 
                    sx={{ 
                      fontWeight: 600,
                      mt: 3,
                      mb: 1
                    }}
                  >
                    Payment Complete
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ opacity: 0.8 }}
                  >
                    Thank you for your payment
                  </Typography>
                </Box>
              </Fade>
            ) : paymentAddress ? (
              <>
                {isUnderpaid && (
                  <Alert
                    severity="warning"
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      alignItems: 'flex-start',
                      '& .MuiAlert-message': { width: '100%' }
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Partial payment received
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Remaining balance{formattedRemainingUsd ? `: $${formattedRemainingUsd}` : ''}. Complete payment with{' '}
                      {underpaidTokenSymbol} on {underpaidNetworkName}. Network and token are locked for this invoice.
                    </Typography>
                  </Alert>
                )}
                {txState.status !== 'idle' && (
                  <Alert
                    severity={txState.status === 'failed' ? 'error' : txState.status === 'confirmed' ? 'success' : 'info'}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      alignItems: 'flex-start',
                      '& .MuiAlert-message': { width: '100%' }
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {txState.status === 'failed'
                        ? 'Transaction failed'
                        : txState.status === 'confirmed'
                        ? 'Transaction confirmed'
                        : 'Transaction submitted'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {txState.status === 'failed'
                        ? (txState.error || 'Transaction failed. Please try again.')
                        : txState.status === 'confirmed'
                        ? 'Payment is being verified...'
                        : 'Waiting for confirmation from the network.'}
                      {txState.explorerUrl && (
                        <span style={{ marginLeft: 8 }}>
                          <a href={txState.explorerUrl} target="_blank" rel="noreferrer">View on explorer</a>
                        </span>
                      )}
                    </Typography>
                  </Alert>
                )}
                <PaymentInfo
                  tokenDetails={tokenDetails}
                  availableTokens={availableTokens}
                  selectedToken={selectedToken}
                  countdown={countdown}
                  checkPayment={{
                    visible: manualVerifyGateOpen && txState.status === 'submitted' && Boolean(txState.hash),
                    status: checkPaymentUi.status,
                    disabled: checkPaymentUi.status === 'checking',
                    onCheck: handleCheckPayment
                  }}
                  onAmountCopy={(message, severity = 'success') => {
                    setSnackbar({
                      open: true,
                      message,
                      severity
                    });
                  }}
                />

                <PaymentMethodWrapper>
                  {/* Quick Payment Card */}
                  <PaymentCard elevation={0} isPrimary={true}>
                    <PaymentCardHeader 
                      expanded={isQuickPayExpanded}
                      onClick={() => handleCardToggle('quick')}
                    >
                      <Box className="header-content">
                        <Box 
                          className="icon-wrapper" 
                          sx={{ 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white'
                          }}
                        >
                          <ElectricBoltIcon />
                        </Box>
                        <Box className="text-content">
                          <Typography className="title">Quick Payment</Typography>
                          <Typography className="subtitle">Connect wallet for instant payment</Typography>
                        </Box>
                      </Box>
                      <ExpandIcon expanded={isQuickPayExpanded} />
                    </PaymentCardHeader>
                    
                    <Collapse in={isQuickPayExpanded}>
                      <PaymentCardContent>
                        {/* BNB Wallet Button */}
                        {selectedNetwork === 'bsc' && (
                          <BNBWalletButton
                            tokenSymbol={selectedTokenInfo?.token_symbol}
                            tokenDecimals={selectedTokenInfo?.token_decimals}
                            tokenAddress={selectedTokenInfo?.token_address}
                            tokenType={selectedTokenInfo?.token_type}
                            amount={tokenDetails.estimatedAmount}
                            paymentAddress={paymentAddress}
                            networkEnvironment={paymentDetails?.network_environment || 'mainnet'}
                            expiresAt={tokenDetails.expiresAt || null}
                            paymentDisabled={isPaymentDisabled}
                            onPaymentInitiated={handlePaymentInitiated}
                            onWalletConnect={(info) => setWalletInfo(info)}
                            onTransactionSubmitted={handleTransactionSubmitted}
                            onTransactionConfirmed={handleTransactionConfirmed}
                            onTransactionFailed={handleTransactionFailed}
                            onError={handlePaymentError}
                            sx={{ 
                              width: '100%',
                              py: 1.5,
                              fontSize: '0.95rem',
                              fontWeight: 600,
                              boxShadow: '0 4px 15px rgba(56, 77, 245, 0.15)',
                              borderRadius: 12,
                              transition: 'all 0.3s ease',
                              background: 'linear-gradient(135deg, #384df5, #4259f7)',
                              textTransform: 'none',
                              letterSpacing: '0.4px',
                              border: 'none',
                              color: 'white',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(56, 77, 245, 0.25)',
                                background: 'linear-gradient(135deg, #4259f7, #2b3eda)'
                              },
                              '&:active': {
                                transform: 'translateY(0)',
                                boxShadow: '0 2px 8px rgba(56, 77, 245, 0.2)',
                              }
                            }}
                          />
                        )}
                        
                        {/* Base Wallet Button */}
                        {selectedNetwork === 'base' && (
                          <BaseWalletButton
                            tokenSymbol={selectedTokenInfo?.token_symbol}
                            tokenDecimals={selectedTokenInfo?.token_decimals}
                            tokenAddress={selectedTokenInfo?.token_address}
                            tokenType={selectedTokenInfo?.token_type}
                            amount={tokenDetails.estimatedAmount}
                            paymentAddress={paymentAddress}
                            networkEnvironment={paymentDetails?.network_environment || 'mainnet'}
                            expiresAt={tokenDetails.expiresAt || null}
                            paymentDisabled={isPaymentDisabled}
                            onPaymentInitiated={handlePaymentInitiated}
                            onWalletConnect={(info) => setWalletInfo(info)}
                            onTransactionSubmitted={handleTransactionSubmitted}
                            onTransactionConfirmed={handleTransactionConfirmed}
                            onTransactionFailed={handleTransactionFailed}
                            onError={handlePaymentError}
                          />
                        )}
                        
                        {/* Solana Wallet Button */}
                        {selectedNetwork === 'solana' && (
                          <SolanaWalletButton
                            tokenSymbol={selectedTokenInfo?.token_symbol}
                            tokenDecimals={selectedTokenInfo?.token_decimals}
                            tokenMintAddress={selectedTokenInfo?.token_address}
                            amount={tokenDetails.estimatedAmount}
                            paymentAddress={paymentAddress}
                            networkEnvironment={paymentDetails?.network_environment || 'mainnet'}
                            sessionToken={sessionToken}
                            sessionSignature={sessionSignature}
                            expiresAt={tokenDetails.expiresAt || null}
                            paymentDisabled={isPaymentDisabled}
                            onPaymentInitiated={handlePaymentInitiated}
                            onTransactionSubmitted={handleTransactionSubmitted}
                            onTransactionConfirmed={handleTransactionConfirmed}
                            onTransactionFailed={handleTransactionFailed}
                            onError={handlePaymentError}
                          />
                        )}
                      </PaymentCardContent>
                    </Collapse>
                  </PaymentCard>

                  {/* Manual Payment Card */}
                  <PaymentCard elevation={0} isPrimary={false}>
                    <PaymentCardHeader 
                      expanded={isManualPayExpanded}
                      onClick={() => handleCardToggle('manual')}
                    >
                      <Box className="header-content">
                        <Box 
                          className="icon-wrapper" 
                          sx={{ 
                            backgroundColor: '#F3F4F6',
                            color: '#6B7280'
                          }}
                        >
                          <QrCodeScannerIcon />
                        </Box>
                        <Box className="text-content">
                          <Typography className="title">Manual Payment</Typography>
                          <Typography className="subtitle">Scan QR or copy address</Typography>
                        </Box>
                      </Box>
                      <ExpandIcon expanded={isManualPayExpanded} />
                    </PaymentCardHeader>
                    
                    <Collapse in={isManualPayExpanded}>
                      <PaymentCardContent>
                        <QRAddressWrapper>
                          {/* QR Code */}
                          <Box sx={{ 
                            p: 2.5, 
                            bgcolor: 'white', 
                            borderRadius: 12,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                          }}>
                            <QRCodeSVG 
                              value={paymentAddress}
                              size={160}
                              bgColor="#FFFFFF"
                              fgColor="#000000"
                              level="L"
                              includeMargin={false}
                            />
                          </Box>
                          
                          {/* Payment Address */}
                          <Box sx={{ 
                            display: 'flex',
                            width: '100%',
                            alignItems: 'center',
                            p: 0.75,
                            bgcolor: 'background.paper',
                            borderRadius: 10,
                            border: '1px solid rgba(0, 0, 0, 0.12)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                          }}>
                            <Typography variant="body2" sx={{ 
                              flexGrow: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontFamily: 'monospace',
                              fontSize: '0.8rem',
                              pl: 1.5
                            }}>
                              {paymentAddress || 'Loading address...'}
                            </Typography>
                            <CopyButton textToCopy={paymentAddress} />
                          </Box>
                          
                          <Typography variant="caption" color="text.secondary" align="center">
                            Send exact amount to this address
                          </Typography>
                        </QRAddressWrapper>
                      </PaymentCardContent>
                    </Collapse>
                  </PaymentCard>
                </PaymentMethodWrapper>

                <PaymentFooter>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 0.8,
                    alignItems: 'center',
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    justifyContent: { xs: 'center', sm: 'flex-start' }
                  }}>
                    <NetworkChip>
                      <CellTowerIcon className="network-icon" />
                      {getNetworkDisplayName(selectedNetwork)}
                    </NetworkChip>
                    <TokenChip>
                      <TokenLogo
                        uri={selectedTokenLogoUri}
                        symbol={selectedTokenInfo?.token_symbol}
                        size={16}
                      />
                      {selectedTokenInfo?.token_symbol}
                    </TokenChip>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 0.8,
                    alignItems: 'center',
                    justifyContent: { xs: 'center', sm: 'flex-end' }
                  }}>
                    <CompactHelpButton
                      onClick={handleHelpClick}
                      size="small"
                    >
                      <HelpOutlineIcon />
                    </CompactHelpButton>
                    {!isUnderpaid && (
                      <ChangeNetworkButton
                        onClick={() => {
                          setPaymentAddress(null);
                          setSelectedNetwork('');
                          setSelectedToken('');
                        }}
                        startIcon={<SwapHorizIcon sx={{ fontSize: '1rem !important' }} />}
                      >
                        Change Network
                      </ChangeNetworkButton>
                    )}
                  </Box>
                </PaymentFooter>

                <Menu
                  anchorEl={helpAnchorEl}
                  open={helpMenuOpen}
                  onClose={handleHelpClose}
                >
                  <MenuItem onClick={handleManualVerify} disabled={isLoading}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RefreshIcon fontSize="small" />
                      <span>Verify Payment</span>
                      {isLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={() => {
                    setShowFAQs(true);
                    handleHelpClose();
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HelpOutlineIcon fontSize="small" />
                      <span>FAQs</span>
                    </Box>
                  </MenuItem>
                </Menu>

                {/* Network Environment Indicator - only show for testnet/devnet */}
                {paymentDetails && paymentDetails.network_environment === 'devnet' && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mt: 1.5,
                    mb: -0.5
                  }}>
                    <Tooltip 
                      title="This payment is using test networks. No real funds will be transferred." 
                      arrow
                      placement="top"
                    >
                      <TestnetChip>
                        Testnet
                        <InfoOutlinedIcon className="icon" fontSize="small" />
                      </TestnetChip>
                    </Tooltip>
                  </Box>
                )}
              </>
            ) : (
              <>
                <Fade in>
                  <PaymentAmount>
                    <CurrencySelector amount={Number(paymentDetails.amount_usd)} />
                    <Typography className="description">
                      {paymentDetails.description}
                    </Typography>
                  </PaymentAmount>
                </Fade>

                {availableTokens.length > 0 && !paymentAddress && !isUnderpaid && (
                  <NetworkTokenSelector
                    availableTokens={availableTokens}
                    selectedNetwork={selectedNetwork}
                    selectedToken={selectedToken}
                    onNetworkSelect={handleNetworkSelect}
                    onTokenSelect={handleTokenSelect}
                  />
                )}
                {isUnderpaid && !paymentAddress && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Loading remaining payment details...
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </ContentWrapper>
        </StyledCard>
        )}
      </>
    );
  }

  return (
    <>
      {content}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

// Helper function to get token info
export const getTokenInfo = (network, environment, symbol) => {
  try {
    // Check if the token exists for the specific environment
    if (NETWORK_TOKENS[network][environment] && 
        NETWORK_TOKENS[network][environment][symbol]) {
      return NETWORK_TOKENS[network][environment][symbol];
    }
    
    // If not found, log the error with detailed information
    console.error(`Token info not found for ${network}/${environment}/${symbol}. Available tokens:`, 
      Object.keys(NETWORK_TOKENS[network][environment] || {}));
      
    return null;
  } catch (error) {
    console.error(`Error retrieving token info for ${network}/${environment}/${symbol}:`, error);
    return null;
  }
};

export default PaymentIframe;
